import type { AnchorHTMLAttributes, MouseEvent, ReactNode } from 'react';
import { confirmDiscardPending } from '../master-data/unsaved-changes';
import { navigate } from '../routing';

/**
 * A link to a screen of the application (a hash such as "#handover/…"): a real link (it can be
 * opened in a new tab or copied), followed inside the application with the same history and the
 * same unsaved-changes question as every other navigation.
 */
export function AppLink({
  to,
  children,
  onNavigate,
  ...rest
}: Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  readonly to: string;
  readonly children: ReactNode;
  readonly onNavigate?: () => void;
}) {
  return (
    <a
      {...rest}
      href={to}
      onClick={(event: MouseEvent<HTMLAnchorElement>) => {
        rest.onClick?.(event);
        // A new tab or window keeps the browser's own behaviour.
        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        )
          return;
        event.preventDefault();
        if (window.location.hash === to) return;
        void confirmDiscardPending().then((leave) => {
          if (!leave) return;
          navigate(to, 'push');
          onNavigate?.();
        });
      }}
    >
      {children}
    </a>
  );
}
